// src/_mixins/locales/routing.ts
import { absoluteUrl } from "../seo/siteUrl";

import { DEFAULT_LANGUAGE, Locale, SUPPORTED_LANGUAGES } from "./constants";

export interface LocaleRouteContext {
	locale: Locale;
	localizedPath: string;
	originalPath: string;
}

// Extract locale from URL
export function extractLocaleFromUrl(url: string): {
	locale: Locale;
	pathWithoutLocale: string;
} {
	// Query string / hash never belong to the logical path.
	const pathOnly = url.split(/[?#]/)[0] || "/";
	// The site runs with Vike's `trailingSlash: true`, so the trailing slash is
	// part of the canonical URL and must survive locale stripping — otherwise the
	// canonical we emit points at a URL that nginx 301-redirects.
	const hasTrailingSlash = pathOnly.length > 1 && pathOnly.endsWith("/");
	const urlParts = pathOnly.replace(/^\/+/, "").split("/").filter(Boolean);
	const firstPart = urlParts[0];

	// Check if first part is a supported locale
	if (firstPart && SUPPORTED_LANGUAGES.includes(firstPart as Locale)) {
		const rest = urlParts.slice(1).join("/");
		return {
			locale: firstPart as Locale,
			pathWithoutLocale: rest ? `/${rest}${hasTrailingSlash ? "/" : ""}` : "/",
		};
	}

	// Default language or no locale prefix
	return {
		locale: DEFAULT_LANGUAGE,
		pathWithoutLocale: pathOnly,
	};
}

/**
 * Add the locale prefix to a locale-FREE path.
 *
 * Passing an already-localized path in would double the prefix
 * (`/hy/about/` → `/hy/hy/about/`), so callers must always hand over the
 * locale-stripped path — `pageContext.localizedPath`.
 */
export function localizeUrl(path: string, locale: Locale): string {
	// Clean path
	const cleanPath = path.replace(/^\/+/, "");

	// For default language, no prefix needed
	if (locale === DEFAULT_LANGUAGE) {
		return "/" + cleanPath;
	}

	// For other languages, add locale prefix. The trailing slash on the locale
	// root (`/hy/`) keeps it consistent with `trailingSlash: true`.
	return `/${locale}${cleanPath ? "/" + cleanPath : "/"}`;
}

/**
 * Absolute alternate URLs for SEO, keyed by locale, built from a locale-FREE path.
 *
 * These are ABSOLUTE and final — do not pass the values back through
 * `localizeUrl`/`getCanonicalUrl`. Doing that is what produced `/hy/hy/about/`
 * in every `hreflang` on the site.
 */
export function generateAlternateUrls(path: string): Record<Locale, string> {
	const urls = {} as Record<Locale, string>;

	SUPPORTED_LANGUAGES.forEach((locale) => {
		urls[locale] = getCanonicalUrl(path, locale);
	});

	return urls;
}

/**
 * Normalise to the trailing-slash form the site actually serves.
 *
 * The app runs with Vike's `trailingSlash: true`, but during prerendering the
 * incoming URL is the slash-less route (`/about`), so canonical and hreflang
 * would otherwise advertise `/about` — a URL nginx answers with a 301 to
 * `/about/`. A canonical that points at a redirect is a wasted signal.
 */
function withTrailingSlash(path: string): string {
	if (path.endsWith("/")) return path;
	// Leave file-like paths alone (e.g. /sitemap.xml).
	const lastSegment = path.split("/").pop() ?? "";
	if (lastSegment.includes(".")) return path;
	return `${path}/`;
}

/**
 * Root-relative path for a locale-FREE path, in the exact form the site serves
 * it (locale prefix + trailing slash).
 *
 * Use this for internal `href`s — linking to the slash-less form would send
 * every visitor and crawler through a 301.
 */
export function localizePath(path: string, locale: Locale): string {
	return withTrailingSlash(localizeUrl(path, locale));
}

/**
 * Absolute canonical URL for a locale-FREE path.
 *
 * Always absolute: hreflang is ignored by Google unless the href is
 * fully-qualified, and og:url/og:image must be absolute for social crawlers.
 * The origin comes from `SITE_URL`, which falls back to the production origin
 * when VITE_APP_URL is absent (as it is in the CI build job).
 */
export function getCanonicalUrl(path: string, locale: Locale): string {
	return absoluteUrl(localizePath(path, locale));
}

// Validate locale
export function isValidLocale(locale: string): locale is Locale {
	return SUPPORTED_LANGUAGES.includes(locale as Locale);
}

// Get locale from various sources
export function detectLocale(urlLocale?: string, acceptLanguage?: string): Locale {
	// 1. URL locale has highest priority
	if (urlLocale && isValidLocale(urlLocale)) {
		return urlLocale;
	}

	// 2. Accept-Language header (server-side)
	if (acceptLanguage) {
		const preferredLocales = acceptLanguage
			.split(",")
			.map((lang) => lang.split(";")[0].trim().toLowerCase())
			.map((lang) => lang.split("-")[0]); // Remove region code

		for (const preferred of preferredLocales) {
			if (isValidLocale(preferred)) {
				return preferred;
			}
		}
	}

	// 3. Default fallback
	return DEFAULT_LANGUAGE;
}

// Create locale redirect
export function createLocaleRedirect(targetPath: string, targetLocale: Locale, status: number = 302): Response {
	const redirectUrl = localizeUrl(targetPath, targetLocale);

	return new Response(null, {
		status,
		headers: {
			Location: redirectUrl,
			"Cache-Control": "no-cache",
		},
	});
}

// Route configuration for Vike
export const localeRouteConfig = {
	// Route patterns
	patterns: {
		localized: "/[locale]/*",
		root: "/*",
	},

	// Route guards
	guards: {
		validateLocale: (locale: string) => isValidLocale(locale),
		requireLocale: (locale?: string) => (locale ? isValidLocale(locale) : false),
	},

	// Route transformers
	transformers: {
		addLocale: (path: string, locale: Locale) => localizeUrl(path, locale),
		removeLocale: (path: string) => extractLocaleFromUrl(path).pathWithoutLocale,
	},
};
